import React from 'react'
import { PQueryBuilder } from '@pushly/aqe'
import type { PQueryBuilderField } from '@pushly/aqe/lib/components/pquery-builder'
import type { PQueryDocument } from '@pushly/models/lib/interfaces/pquery-document'
import {
    SEGMENT_VALUE_INPUTS,
    SEGMENT_VALUE_VALIDATORS,
    useSegmentValueSummaries,
} from '../../../components/segment-query-builder/value-inputs'
import { JOURNEY_QUERY_CONSTRAINTS } from '../journey-query-constraints'
import { EventPropertyProvider } from './event-property-context'
import { EVENT_PROPERTY_FIELD_GROUPS } from './event-property-fields'
import { EVENT_BINDABLE_INPUT_KEY } from './event-bindable-fields'
import {
    createEventBindingSummary,
    EVENT_BINDABLE_VALUE_INPUTS,
    EVENT_BINDABLE_VALUE_VALIDATORS,
} from './event-bindable-value-input'

/**
 * The value editors reachable from a condition authored against an external event.
 *
 * Segmentation's own registry plus the event-bindable editor, composed rather than merged into it:
 * a segment authored anywhere else resolves exactly the entries it resolves today, because nothing
 * in its catalog names the event key.
 */
export const EVENT_CONDITION_VALUE_INPUTS = {
    ...SEGMENT_VALUE_INPUTS,
    ...EVENT_BINDABLE_VALUE_INPUTS,
}

export const EVENT_CONDITION_VALUE_VALIDATORS = {
    ...SEGMENT_VALUE_VALIDATORS,
    ...EVENT_BINDABLE_VALUE_VALIDATORS,
}

export interface EventConditionBuilderProps {
    /** The stored query document, in the form the router reads it back in. */
    value: PQueryDocument | undefined
    onChange: (next: PQueryDocument) => void
    /** The fields conditions may be written against. */
    fields: PQueryBuilderField[]
    /**
     * The event type's registered properties, offered as values wherever the catalog marks a field
     * event-bindable. Empty for a builder whose conditions name event properties directly.
     */
    eventProperties?: PQueryBuilderField[]
    disabled?: boolean
}

const NO_EVENT_PROPERTIES: PQueryBuilderField[] = []

/**
 * The document a builder opens on when the trigger carries none yet.
 *
 * One shared instance rather than a literal per render: the builder re-parses whenever the document
 * it is handed is a different object, so a fresh literal on every render would re-initialise the
 * store under the reader mid-edit.
 *
 * An empty group rather than an empty object. The builder derives its root from what it is handed,
 * so `{}` leaves it with no group to hold rules and it renders nothing — no rows, and no way to add
 * one.
 */
const EMPTY_DOCUMENT: Record<string, unknown> = { and: [] }

/**
 * A condition builder for the two queries an external-event trigger carries: the filter over the
 * event's own properties, and the entry criteria over subscribers that may bind to them.
 *
 * One component for both because they differ only in what they are handed — a catalog of event
 * properties in one case, a catalog of subscriber fields marked event-bindable in the other. The
 * builder itself is unchanged: an event value source is catalog data plus a registered editor.
 */
export const EventConditionBuilder: React.FC<EventConditionBuilderProps> = ({
    value,
    onChange,
    fields,
    eventProperties = NO_EVENT_PROPERTIES,
    disabled,
}) => {
    const segmentSummaries = useSegmentValueSummaries()

    // Rebuilt when the registry lands, because a summary reads a property's name out of it — a row
    // opened before the event type resolved would otherwise keep reading the raw path forever.
    const valueSummaries = React.useMemo(
        () => ({
            ...segmentSummaries,
            [EVENT_BINDABLE_INPUT_KEY]: createEventBindingSummary(eventProperties, segmentSummaries),
        }),
        [segmentSummaries, eventProperties],
    )

    return (
        <EventPropertyProvider properties={eventProperties}>
            <PQueryBuilder
                value={(value ?? EMPTY_DOCUMENT) as Record<string, unknown>}
                fields={fields}
                fieldGroups={EVENT_PROPERTY_FIELD_GROUPS}
                valueInputs={EVENT_CONDITION_VALUE_INPUTS}
                valueSummaries={valueSummaries}
                valueValidators={EVENT_CONDITION_VALUE_VALIDATORS}
                constraints={JOURNEY_QUERY_CONSTRAINTS}
                disabled={disabled}
                onRuleCommit={(next) => onChange(next as PQueryDocument)}
            />
        </EventPropertyProvider>
    )
}
